import { useState } from "react";
import { BadgeCheck, Zap } from "lucide-react";
import { useAuth } from "../auth/AuthProvider";
import { useOrganization } from "../organization/useOrganization";
import { Button } from "../../shared/ui/Button";
import { Card, EmptyState } from "../../shared/ui/Card";
import { PageSkeleton, StatusBadge } from "../../shared/ui/Status";
import { useIncentiveActions, useIncentives } from "./useIncentives";
export function EnergyApprovalPanel() {
  const { session } = useAuth();
  const org = useOrganization(),
    query = useIncentives(),
    actions = useIncentiveActions();
  const [reasons, setReasons] = useState<Record<string, string>>({}),
    [notice, setNotice] = useState(""),
    [error, setError] = useState("");
  if (!session || !org.data || !query.data) return <PageSkeleton />;
  const claims = query.data.claims.filter(
      (x) =>
        x.status === "pending" &&
        (session.profile.role === "super" ||
          x.storeId === session.profile.storeId),
    ),
    appeals = query.data.appeals.filter(
      (x) =>
        x.status === "pending" &&
        (session.profile.role === "super" ||
          x.storeId === session.profile.storeId),
    ),
    names = Object.fromEntries(org.data.accounts.map((x) => [x.id, x.name]));
  const review = async (id: string, decision: "approve" | "reject") => {
    setNotice("");
    setError("");
    try {
      await actions.reviewClaim({
        claimId: id,
        decision,
        reason: reasons[id] ?? "",
      });
      setNotice(
        decision === "approve"
          ? "审批通过，星能已写入员工流水。"
          : "申报已驳回。",
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "审批失败");
    }
  };
  const reviewAppeal = async (id: string, decision: "approve" | "reject") => {
    setNotice("");
    setError("");
    try {
      await actions.reviewAppeal({
        appealId: id,
        decision,
        reason: reasons[id] ?? "",
      });
      setNotice(
        decision === "approve"
          ? "申诉通过，星能已幂等补发。"
          : "申诉复核后维持驳回。",
      );
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "申诉复核失败");
    }
  };
  return (
    <section className="learning-panel energy-approval">
      <div className="section-divider">
        <span>激励治理</span>
        <h2>星能审批</h2>
      </div>
      {(notice || error) && (
        <div
          className={
            error ? "inline-notice inline-notice--error" : "inline-notice"
          }
        >
          {error || notice}
        </div>
      )}
      {claims.length ? (
        <div className="approval-list">
          {claims.map((c) => {
            const r = query.data!.rules.find((x) => x.id === c.ruleId);
            return (
              <Card key={c.id}>
                <div className="approval-order">
                  <Zap />
                  <div>
                    <span>
                      {r?.name} · +{r?.points}
                    </span>
                    <h2>{names[c.employeeId]}</h2>
                    <p>
                      {c.evidence} · 成果日期 {c.occurredOn}
                    </p>
                    {c.evidencePreviewUrl && (
                      <a className="evidence-preview evidence-preview--approval" href={c.evidencePreviewUrl} target="_blank" rel="noreferrer">
                        <img src={c.evidencePreviewUrl} alt={`${names[c.employeeId]}的星能申报照片凭证`} loading="lazy" />
                        <span>点击预览原始凭证</span>
                      </a>
                    )}
                  </div>
                  <StatusBadge tone="warning">待核验</StatusBadge>
                </div>
                <label className="v4-field">
                  <span>审批说明（驳回必填）</span>
                  <input
                    value={reasons[c.id] ?? ""}
                    onChange={(e) =>
                      setReasons({ ...reasons, [c.id]: e.target.value })
                    }
                    placeholder="填写核验结果或驳回原因"
                  />
                </label>
                <div className="approval-actions">
                  <Button
                    variant="ghost"
                    onClick={() => review(c.id, "reject")}
                    disabled={actions.pending}
                  >
                    驳回
                  </Button>
                  <Button
                    onClick={() => review(c.id, "approve")}
                    disabled={actions.pending}
                  >
                    <BadgeCheck size={15} />
                    通过并入账
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <EmptyState
          title="暂无星能审批"
          description="员工提交成果申报后会显示在这里。"
        />
      )}
      <div className="section-divider">
        <span>驳回复核</span>
        <h2>星能申诉</h2>
      </div>
      {appeals.length ? (
        <div className="approval-list">
          {appeals.map((appeal) => {
            const claim = query.data!.claims.find(
              (item) => item.id === appeal.claimId,
            );
            const rule = query.data!.rules.find(
              (item) => item.id === claim?.ruleId,
            );
            return (
              <Card key={appeal.id}>
                <div className="approval-order">
                  <Zap />
                  <div>
                    <span>
                      {rule?.name} · +{rule?.points}
                    </span>
                    <h2>{names[appeal.employeeId]}</h2>
                    <p>
                      原驳回：{claim?.reviewReason || "未填写"} · 申诉：
                      {appeal.reason}
                    </p>
                  </div>
                  <StatusBadge tone="warning">待复核</StatusBadge>
                </div>
                <label className="v4-field">
                  <span>复核说明（维持驳回必填）</span>
                  <input
                    value={reasons[appeal.id] ?? ""}
                    onChange={(event) =>
                      setReasons({
                        ...reasons,
                        [appeal.id]: event.target.value,
                      })
                    }
                  />
                </label>
                <div className="approval-actions">
                  <Button
                    variant="ghost"
                    disabled={actions.pending}
                    onClick={() => reviewAppeal(appeal.id, "reject")}
                  >
                    维持驳回
                  </Button>
                  <Button
                    disabled={actions.pending}
                    onClick={() => reviewAppeal(appeal.id, "approve")}
                  >
                    <BadgeCheck size={15} />
                    通过并补发
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <EmptyState
          title="暂无星能申诉"
          description="员工对已驳回申报提交申诉后会显示在这里。"
        />
      )}
    </section>
  );
}
